/**
 * 갤러리 한 장의 이미지를 나타내는 단일 진실 모델.
 *
 * 기존 파이프라인이 `form.image_urls` + `thumbnailsByUrl` + `seqByCompletedUrl` +
 * `completedBySeqRef` + 업로드 큐 items 로 흩어 두던 상태를 하나로 합친다.
 *
 * 순서는 오직 `seq` 로 표현한다 (선택 시점에 부여되고 이후 불변). 커밋되는 `image_urls`
 * 배열은 이 아이템들의 projection 이다.
 */
export type ImageItemLifecycle = "existing" | "uploading" | "uploaded";

export interface ImageUploadState {
  status: "pending" | "uploading" | "error";
  /** 0–100 */
  progress: number;
  error?: string;
}

export interface ImageItem {
  /** 안정적 식별자. React key + 스토어 연산용. existing 은 url 에서 파생, 업로드는 큐 item id. */
  readonly id: string;
  /** 선택/표시 순서. monotonic, 부여 후 불변. */
  readonly seq: number;
  readonly lifecycle: ImageItemLifecycle;
  readonly isHeic: boolean;
  /** 업로드 중 미리보기·재시도에 필요한 원본 File. uploading 일 때만 존재. */
  readonly file?: File;
  /** 클라이언트 blob 미리보기. 업로드 중 또는 원격 썸네일이 준비되기 전 uploaded 아이템에서 사용. */
  readonly localPreviewUrl?: string;
  /** 서버 커밋 URL. uploaded=S3 path(.heic 가능), existing=image_urls 엔트리. */
  readonly originalUrl?: string;
  /** 서버 webp 썸네일 (Celery; finalize 응답에서 즉시 알려지나 파일은 잠시 404). HEIC 일 때만. */
  readonly webpUrl?: string;
  /** lifecycle === "uploading" 일 때만 존재. */
  readonly upload?: ImageUploadState;
}

const HEIC_EXTENSION_PATTERN = /\.(heic|heif)$/i;

/** URL 확장자로 HEIC 추정 (서버 기존 이미지는 보통 webp 라 false). */
export function isHeicUrl(url: string): boolean {
  const pathname = url.split("?")[0] ?? "";
  return HEIC_EXTENSION_PATTERN.test(pathname);
}

interface PostProcessResult {
  image_upload_path: string | null;
  webp_image_upload_path: string | null;
  webp_image_upload_path_resized: string | null;
}

/**
 * finalize 응답에서 webp 썸네일 URL 을 추출한다 (HEIC 일 때만).
 *
 * 기존 finalizer 와 동일: 비-HEIC 은 원본이 이미 표시 가능하므로 썸네일을 두지 않는다.
 * HEIC 은 `resized > webp > image_path` 순으로 우선.
 */
export function extractWebpThumbnail(postProcess: PostProcessResult, isHeic: boolean): string | undefined {
  if (!isHeic) return undefined;
  return (
    postProcess.webp_image_upload_path_resized ||
    postProcess.webp_image_upload_path ||
    postProcess.image_upload_path ||
    undefined
  );
}

/* ------------------------------------------------------------------ */
/*  순수 스토어 변환 (ImageItem[] → ImageItem[])                         */
/* ------------------------------------------------------------------ */

/** 기존 form.image_urls 를 existing 아이템(선행 seq 블록)으로 변환. */
export function seedExistingItems(urls: string[]): ImageItem[] {
  return urls.map((url, index) => ({
    id: `existing:${url}`,
    seq: index,
    lifecycle: "existing" as const,
    isHeic: isHeicUrl(url),
    originalUrl: url,
  }));
}

/**
 * 외부 form.image_urls 변경을 스토어에 반영한다.
 *
 * 이미 알고 있는 committed 아이템은 webpUrl/lifecycle 같은 메타데이터를 보존하고,
 * 새 URL 은 existing 아이템으로 추가한다. 업로드 중 아이템은 form projection 에 없으므로
 * committed 블록 뒤에 기존 상대 순서를 유지해 붙인다.
 */
export function reconcileCommittedUrls(items: ImageItem[], urls: string[]): ImageItem[] {
  const usedIds = new Set<string>();

  const committed = urls.map((url, index) => {
    const existing = items.find((it) => it.lifecycle !== "uploading" && it.originalUrl === url && !usedIds.has(it.id));
    if (existing) {
      usedIds.add(existing.id);
      return { ...existing, seq: index };
    }

    return {
      id: `existing:${url}`,
      seq: index,
      lifecycle: "existing" as const,
      isHeic: isHeicUrl(url),
      originalUrl: url,
    };
  });

  const uploading = sortedItems(items)
    .filter((it) => it.lifecycle === "uploading")
    .map((it, offset) => ({ ...it, seq: urls.length + offset }));

  return [...committed, ...uploading];
}

/** 다음에 부여할 seq (현재 최대 seq + 1). 빈 배열이면 0. */
export function nextSeq(items: ImageItem[]): number {
  return items.reduce((max, it) => Math.max(max, it.seq), -1) + 1;
}

/** id 로 아이템을 patch. 없으면 그대로. */
export function patchItem(items: ImageItem[], id: string, patch: Partial<ImageItem>): ImageItem[] {
  return items.map((it) => (it.id === id ? { ...it, ...patch } : it));
}

/** id 로 아이템 제거. */
export function removeItemById(items: ImageItem[], id: string): ImageItem[] {
  return items.filter((it) => it.id !== id);
}

/**
 * 커밋되는 image_urls 로 projection.
 *
 * 업로드 진행 중(uploading)은 제외하고, seq 순서로 정렬해 originalUrl 만 뽑는다.
 * 완료 순서와 무관하게 선택 순서(seq)가 보존된다.
 */
export function projectCommittedUrls(items: ImageItem[]): string[] {
  return items
    .filter((it) => it.lifecycle !== "uploading")
    .slice()
    .sort((a, b) => a.seq - b.seq)
    .map((it) => it.originalUrl)
    .filter((url): url is string => !!url);
}

/** 표시용 정렬된 아이템 (seq 오름차순). */
export function sortedItems(items: ImageItem[]): ImageItem[] {
  return items.slice().sort((a, b) => a.seq - b.seq);
}
